import { useEffect, useState } from "react";
import axios from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import { Loader2 } from "lucide-react";
import { AdminModal } from "../../admin-panel/components/AdminModal";

interface CredentialsModalProps {
  onClose: () => void;
  passwordOnly?: boolean; // If true, skip credentials step and go straight to password setup
}

export default function CredentialsModal({ onClose, passwordOnly = false }: CredentialsModalProps) {
  const { checkAuth, user } = useAuth();
  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
    name: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [step, setStep] = useState<"credentials" | "password">(passwordOnly ? "password" : "credentials");
  const [showModal, setShowModal] = useState(false);

  // ------------------- CHECK IF PASSWORD RESET REQUIRED -------------------
  useEffect(() => {
    if (passwordOnly && user?.password_reset_required) {
      setShowModal(true);
      setStep("password");
    }
  }, [passwordOnly, user?.password_reset_required]);

  // ------------------- CHECK FIRST TIME VISIT (only if not passwordOnly) -------------------
  useEffect(() => {
    if (!passwordOnly) {
      const alreadySeen = sessionStorage.getItem("credentialsModalSeen");
      if (!alreadySeen) {
        setShowModal(true); // show modal only if not seen
        sessionStorage.setItem("credentialsModalSeen", "true");
      }
    }
  }, [passwordOnly]);

  // ------------------- FETCH CREDENTIALS (only if not passwordOnly) -------------------
  useEffect(() => {
    if (!showModal || passwordOnly) return; // don't fetch if modal not shown or password-only mode

    const fetchCredentials = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/credentials");
        setCredentials(response.data);
        setStep("credentials");
      } catch (err: any) {
        if (err?.response?.status === 404) {
          onClose(); // No credentials → close modal
          return;
        }
        setError(err?.response?.data?.error || "Failed to load credentials. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [showModal, onClose, passwordOnly]);

  const copyToClipboard = (text: string, type: "email" | "password") => {
    navigator.clipboard.writeText(text);
    if (type === "email") {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } else {
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };

  const handleContinue = () => {
    setStep("password");
    setError(null);
  };

  // ------------------- SUBMIT NEW PASSWORD -------------------
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!password || !passwordConfirmation) {
      setError("Please complete all required fields.");
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8 || password.length > 12) {
      setError("Password must be 8-12 characters long.");
      return;
    }
    if (!/(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(password)) {
      setError(
        "Password must contain at least one uppercase letter, one number, and one special character (!@#$%^&*).",
      );
      return;
    }
 

    setSubmitting(true);
    try {
      await axios.post("/set-password", {
        password,
        password_confirmation: passwordConfirmation,
      });

      // Refresh auth to update user state
      await checkAuth();

      // Close modal and refresh page after successful password set
      setShowModal(false);
      window.location.reload();
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data?.errors?.password?.[0];
      setError(apiMessage || "Failed to set password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!showModal) return null;

  // In password-only mode, skip loading and credentials check
  if (!passwordOnly) {
    if (loading) {
      return (
        <AdminModal open={showModal} onClose={() => {}} closeOnOverlayClick={false} closeOnEscape={false}>
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 opacity-100 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33]">
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          </div>
        </AdminModal>
      );
    }

    if (error && !credentials) {
      return (
        <AdminModal open={showModal} onClose={onClose}>
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 opacity-100 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33]">
            <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
              {error}
            </div>
            <div className="mt-4 text-right">
              <Button onClick={onClose} size="sm">Close</Button>
            </div>
          </div>
        </AdminModal>
      );
    }

    if (!credentials) return null;
  }

  // ------------------- MODAL CONTENT -------------------
  return (
    <AdminModal open={showModal} onClose={onClose} closeOnOverlayClick={false} closeOnEscape={false}>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 opacity-100 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33] max-h-[90vh] overflow-y-auto lg:p-11">
        {step === "credentials" && (
          <>
            <div className="mb-4">
              <h2 className="mb-2 text-lg font-semibold text-gray-600 dark:text-white">Welcome! 🎉</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Your account has been created. Save your credentials before continuing.</p>
            </div>

            <div className="p-4 mb-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-[var(--rule)]">
              <h3 className="mb-3 text-sm font-semibold text-gray-800 dark:text-white">Account Credentials</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600 dark:text-gray-400">Email</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="text" value={credentials.email} readOnly className="flex-1 bg-white dark:bg-gray-700" />
                    <Button type="button" size="sm" variant="outline" onClick={() => copyToClipboard(credentials.email, "email")}>{emailCopied ? "Copied!" : "Copy"}</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 dark:text-gray-400">Temporary Password</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input type={showPassword ? "text" : "password"} value={credentials.password} readOnly className="pr-10 bg-white dark:bg-gray-700" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">{showPassword ? "👁️" : "👁️‍🗨️"}</button>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={() => copyToClipboard(credentials.password, "password")}>{passwordCopied ? "Copied!" : "Copy"}</Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button onClick={handleContinue} size="sm">Continue to Set Password</Button>
            </div>
          </>
        )}

        {step === "password" && (
          <>
            <div className="mb-4">
              <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white">Set Your Password</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Create a new password for your account.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">{error}</div>}
              <div>
                <Label>New Password <span className="text-error-500">*</span></Label>
                <Input type="password" value={password} placeholder="Enter new password" onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div>
                <Label>Confirm Password <span className="text-error-500">*</span></Label>
                <Input type="password" value={passwordConfirmation} placeholder="Confirm new password" onChange={(e) => setPasswordConfirmation(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                {/* <Button type="button" variant="outline" onClick={() => setStep("credentials")} size="sm">Back</Button> */}
                <Button type="submit" size="sm" disabled={submitting}>
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                      Setting…
                    </span>
                  ) : (
                    "Set Password"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </AdminModal>
  );
}
